import { isNil } from 'lodash';
import React, { useContext } from 'react';
import { View as RNView } from 'react-native';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, SharedStyles, grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { MBTextH4Semibold } from '../typography';

interface Props {
  title?: string;
  leftIcon?: MBIconV2Name;
  leftIconColor?: string;
  leftIconSize?: number;
  leftTitlePosition?: number;
  onLeftIconPress?: (node: React.Component) => void;
  rightIcon?: MBIconV2Name;
  rightIconColor?: string;
  rightIconSize?: number;
  rightButtonText?: string;
  rightTitlePosition?: number;
  titleSize?: number;
  onRightIconPress?: (node: React.Component) => void;
  borderBottomWidth?: number;
  height?: number;
}

// number of grid units to allow for left and right icon/button
const HORIZONTAL_TITLE_OFFSET = 13;
const DEFAULT_ICON_SIZE = 5;

export function MBBottomSheetTitleBar({
  title,
  leftIcon,
  leftIconColor,
  leftIconSize,
  leftTitlePosition,
  onLeftIconPress,
  rightIcon,
  rightIconColor,
  rightIconSize,
  rightButtonText,
  rightTitlePosition,
  titleSize,
  onRightIconPress,
  borderBottomWidth = 1,
  height,
}: Props) {
  const colorTheme = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const defaultHeight = isDesktop ? grid(6) : grid(8);
  const defaultTitleSize = isDesktop ? 16 : 20;

  return (
    <RNView
      style={[
        {
          borderBottomWidth: borderBottomWidth,
          borderColor: colorTheme.section.border,
        },
        SharedStyles.centerChildren,
      ]}
    >
      <View style={{ height: height ?? defaultHeight }} center row fullWidth>
        {leftIcon && onLeftIconPress && (
          <View style={{ position: 'absolute', left: 0, zIndex: 2 }} paddingHorizontal={2}>
            <MBTouchableOpacity onPress={onLeftIconPress}>
              <MBIconV2
                name={leftIcon}
                size={leftIconSize ?? grid(DEFAULT_ICON_SIZE)}
                color={leftIconColor || colorTheme.foregroundActive}
              />
            </MBTouchableOpacity>
          </View>
        )}
        <View
          style={{
            position: 'absolute',
            left:
              !isNil(leftIcon) && !isNil(rightIcon)
                ? grid(HORIZONTAL_TITLE_OFFSET)
                : leftTitlePosition ?? grid(2),

            right:
              !isNil(leftIcon) && !isNil(rightIcon)
                ? grid(HORIZONTAL_TITLE_OFFSET)
                : rightTitlePosition ?? grid(2),
          }}
          centerHorizontally
        >
          <MBTextH4Semibold style={{ fontSize: titleSize ?? defaultTitleSize }} numberOfLines={1}>
            {title}
          </MBTextH4Semibold>
        </View>
        {rightIcon && onRightIconPress && (
          <View paddingRight={2} style={{ position: 'absolute', right: 0 }}>
            <MBTouchableOpacity onPress={onRightIconPress}>
              <MBIconV2
                name={rightIcon}
                size={rightIconSize ?? grid(DEFAULT_ICON_SIZE)}
                color={rightIconColor || colorTheme.foregroundActive}
              />
            </MBTouchableOpacity>
          </View>
        )}
        {!isNil(onRightIconPress) && !isNil(rightButtonText) && !rightIcon && (
          <View style={{ position: 'absolute', right: grid(2) }}>
            <MBButton
              size="small"
              theme="normal"
              onPress={onRightIconPress}
              title={rightButtonText}
            />
          </View>
        )}
      </View>
    </RNView>
  );
}
